'use client';
import { Box } from '@mui/material';
import { useMemo } from 'react';
import {
  getIsHysaCalculatorActive,
  getIsShowLessMonetizingOffersActive,
  getShowSevenOffersInSummaryTableActive,
} from '@repo/hooks/featureFlags/featureFlags';
import { useSequencingContext } from '@repo/sequencing/client/context/SequencingContext';
import { getSortedProductCardSections, getSortedSummaryTableRows } from '@repo/sequencing/utils';
import type { Vertical } from '@repo/supergraph/marketplace/constants';
import type { MultiProductBannerOfferType } from '@repo/supergraph/marketplace/multiProductBanner/types';
import type { StateAbbreviation } from '@repo/supergraph/marketplace/types';
import type { CrossSellSection, Roundup } from '@repo/supergraph/roundup/base/types';
import { MIN_CROSS_SELL_PRODUCT_CARDS } from '@repo/supergraph/roundup/constants';
import { GeoDropdown } from '@repo/ui/GeoTargeting/GeoDropdown';
import { useGeo } from '@repo/ui/GeoTargeting/GeoProvider';
import { AdditionalRecommendations } from '@repo/ui/roundups/components/AdditionalRecommendations';
import { SummaryTableSection } from '@repo/ui/roundups/components/SummaryTable/SummaryTableSection';
import type { MobileOrDesktop } from '@repo/utils/deviceType/types';
import { BankingAssessmentBanner } from '../../../BankingAssessmentBanner/BankingAssessmentBanner';
import { BankingAssessmentBannerControlTracker } from '../../../BankingAssessmentBanner/BankingAssessmentBannerControlTracker';
import { PromotionalBanner } from '../../../PromotionalBanner/PromotionalBanner';
import { PromotionalControlTracker } from '../../../PromotionalBanner/PromotionalControlTracker';
import type { PromotionalBannerSignUpOptions } from '../../../PromotionalBanner/types';
import { ProductCardCategorySectionsWrapper } from '../../../SummaryTableProductCardsWrapper/ProductCardCategorySectionsWrapper';
import { SummaryTableSectionWrapper } from '../../../SummaryTableProductCardsWrapper/SummaryTableSectionWrapper';
import { CompoundCalculatorSection } from '../CalculatorSection/CompoundCalculatorSection';
import { CrossSellModule } from '../CrossSellModule/CrossSellModule';
import { MultiProductBanner } from '../MultiProductBanner';
import { getFilterSlugForPage } from '../OffersFilter/utils';
import ProductCardCategorySections from '../ProductCardCategorySections';
import { ProductsFiltersWrapper } from './ProductsFiltersWrapper';
import { SectionTitle } from './SectionTitle';
import { getShowLessMonetizingOffersProps, getShowSevenOffersInSummaryTableProps } from './utils';

interface ProductsSectionProps {
  rows: Roundup['summaryTableSection']['rows'];
  sectionTitle: string;
  isSeoBankingFFActive: boolean;
  isShowingAdditionalRecommendations: boolean;
  productCardsSection: {
    categorySections: Roundup['productCardsSection']['categorySections'];
  };
  additionalRecommendations: Roundup['additionalRecommendations'];
  crossSellSection?: CrossSellSection;
  showSummaryTable: boolean;
  shouldHideTitle?: boolean;
  slug: string;
  initialLocation?: StateAbbreviation;
  multiProductBannerOffers?: MultiProductBannerOfferType[];
  pageUrl: string;
  vertical: Vertical;
  deviceType?: MobileOrDesktop;
  highestRate?: number;
  isBankingAssessmentTreatment1Enabled?: boolean;
  isBankingAssessmentTreatment2Enabled?: boolean;
  isAccountCreationEntrypointsTreatment1Enabled?: boolean;
  isAccountCreationEntrypointsTreatment2Enabled?: boolean;
  isAccountCreationEntrypointsTreatment3Enabled?: boolean;
  accountCreationSignUpOptions?: PromotionalBannerSignUpOptions;
  hideFilterUI?: boolean;
}

export const ProductsSection: React.FC<ProductsSectionProps> = ({
  additionalRecommendations,
  rows,
  crossSellSection,
  sectionTitle,
  isSeoBankingFFActive,
  isShowingAdditionalRecommendations,
  productCardsSection,
  showSummaryTable,
  shouldHideTitle,
  slug,
  highestRate,
  initialLocation,
  multiProductBannerOffers,
  pageUrl,
  vertical,
  deviceType,
  isBankingAssessmentTreatment1Enabled = false,
  isBankingAssessmentTreatment2Enabled = false,
  isAccountCreationEntrypointsTreatment1Enabled = false,
  isAccountCreationEntrypointsTreatment2Enabled = false,
  isAccountCreationEntrypointsTreatment3Enabled = false,
  accountCreationSignUpOptions,
  hideFilterUI = false,
}) => {
  const { regionCode } = useGeo();
  const showCrossSellSection =
    crossSellSection &&
    crossSellSection.productCards &&
    crossSellSection.productCards.length >= MIN_CROSS_SELL_PRODUCT_CARDS;
  const { sequence: sequenceToDisplay, isLoadingAsyncSequence } = useSequencingContext();

  const sortedRows = useMemo(
    () => getSortedSummaryTableRows(rows, sequenceToDisplay.summaryTable),
    [sequenceToDisplay.summaryTable],
  );

  const sortedProductCardSections = useMemo(
    () => getSortedProductCardSections(productCardsSection, sequenceToDisplay.productCards),
    [sequenceToDisplay.productCards],
  );

  const highestAPY = highestRate;

  const isFilterEnabled = Boolean(getFilterSlugForPage(slug));
  const isHysaCalculatorActive = getIsHysaCalculatorActive(slug);

  const renderSummaryTableSection = () => {
    if (!showSummaryTable) {
      return null;
    }

    const commonProps = {
      sectionTitle,
      regionCode,
      slug,
      showSkeleton: isLoadingAsyncSequence,
      pageUrl,
      ...(getIsShowLessMonetizingOffersActive(slug, deviceType)
        ? getShowLessMonetizingOffersProps(sortedRows)
        : {}),
      ...(getShowSevenOffersInSummaryTableActive(slug)
        ? getShowSevenOffersInSummaryTableProps()
        : {}),
    };

    if (isFilterEnabled) {
      return <SummaryTableSectionWrapper {...commonProps} />;
    }

    return (
      <SummaryTableSection
        {...commonProps}
        rows={sortedRows}
        shouldDisplayPromoButton={isAccountCreationEntrypointsTreatment3Enabled}
        signUpOptions={accountCreationSignUpOptions}
      />
    );
  };

  const renderProductCardSections = () => {
    const commonProps = {
      categorySections: sortedProductCardSections.categorySections,
      isSeoBankingFFActive: isSeoBankingFFActive,
      regionCode: regionCode,
      showSkeleton: isLoadingAsyncSequence,
      slug: slug,
    };

    if (isFilterEnabled) {
      return <ProductCardCategorySectionsWrapper {...commonProps} />;
    }

    return <ProductCardCategorySections {...commonProps} />;
  };

  return (
    <Box component="section" aria-labelledby="summary-table-product-cards-title">
      {!!initialLocation && !isFilterEnabled && (
        <Box sx={{ maxWidth: { xs: '100%', md: '82%' }, margin: 'auto', marginBottom: 4 }}>
          <GeoDropdown initialLocation={initialLocation} />
        </Box>
      )}
      {!shouldHideTitle && (
        <SectionTitle sectionTitle={sectionTitle} id="summary-table-product-cards-title" />
      )}
      <ProductsFiltersWrapper
        sortedRows={sortedRows}
        slug={slug}
        initialLocation={initialLocation}
        vertical={vertical}
        hideFilterUI={hideFilterUI}
      >
        {renderSummaryTableSection()}
        {showCrossSellSection && (
          <Box
            sx={{
              maxWidth: { xs: '100%', md: '82%' },
              margin: 'auto',
              marginTop: 5,
              marginBottom: 8,
              paddingBottom: 2,
            }}
          >
            <CrossSellModule crossSellSection={crossSellSection} regionCode={regionCode} />
          </Box>
        )}
        <PromotionalBanner
          isTreatment1Enabled={isAccountCreationEntrypointsTreatment1Enabled}
          isTreatment2Enabled={isAccountCreationEntrypointsTreatment2Enabled}
          signUpOptions={accountCreationSignUpOptions}
        />
        {!isAccountCreationEntrypointsTreatment1Enabled &&
          !isAccountCreationEntrypointsTreatment2Enabled &&
          !isAccountCreationEntrypointsTreatment3Enabled && (
            <PromotionalControlTracker slug={slug} />
          )}
        <MultiProductBanner
          slug={slug}
          multiProductBannerOffers={multiProductBannerOffers}
          deviceType={deviceType}
        />
        {isBankingAssessmentTreatment1Enabled || isBankingAssessmentTreatment2Enabled ? (
          <Box sx={{ maxWidth: { xs: '100%', md: '82%' }, marginX: 'auto', marginBottom: 8 }}>
            <BankingAssessmentBanner
              isTreatment1Enabled={isBankingAssessmentTreatment1Enabled}
              isTreatment2Enabled={isBankingAssessmentTreatment2Enabled}
              slug={slug}
            />
          </Box>
        ) : (
          <BankingAssessmentBannerControlTracker slug={slug} />
        )}
        {isHysaCalculatorActive && (
          <CompoundCalculatorSection deviceType={deviceType} defaultApy={highestAPY} />
        )}
        {renderProductCardSections()}
        {isShowingAdditionalRecommendations && additionalRecommendations && (
          <AdditionalRecommendations
            additionalRecommendations={additionalRecommendations}
            vertical={vertical}
          />
        )}
      </ProductsFiltersWrapper>
    </Box>
  );
};
